"use client";

import { useState, useRef, useEffect } from "react";
import {
  MdNotifications,
  MdPersonAdd,
  MdAssignment,
  MdCheckCircle,
  MdWarning,
} from "react-icons/md";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { makeAuthenticatedRequest } from "@/utils/auth";
import Notification from "../../../../public/images/icon/notifiction.svg";
import Image from "next/image";

dayjs.extend(relativeTime);

export default function NotificationDropdown() {
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const fetchUnreadCount = async () => {
    try {
      const response = await makeAuthenticatedRequest(
        `${process.env.NEXT_PUBLIC_API_END_POINT}/notifications/count`,
      );
      if (!response) return;
      const data = await response.json();
      if (response.ok) {
        setUnreadCount(data.unreadCount || 0);
      }
    } catch (error) {
    }
  };

  useEffect(() => {
    fetchUnreadCount();

    // SSE se real-time notification count update
    const token = localStorage.getItem("token");
    if (!token) return;

    const eventSource = new EventSource(
      `${process.env.NEXT_PUBLIC_API_END_POINT}/notifications/stream?token=${token}`
    );

    eventSource.onmessage = (e) => {
      if (e.data === "connected") return;
      try {
        const data = JSON.parse(e.data);
        if (data.notifications?.length) {
          setUnreadCount((prev) => prev + data.notifications.length);
        }
      } catch {}
    };

    return () => eventSource.close();
  }, []);

  const fetchNotifications = async () => {
    if (loading) return; // Prevent multiple simultaneous requests

    try {
      setLoading(true);
      const response = await makeAuthenticatedRequest(
        `${process.env.NEXT_PUBLIC_API_END_POINT}/notifications`,
      );

      if (!response) return;

      const data = await response.json();
      if (response.ok) {
        setNotifications(data.notifications);
        setUnreadCount(data.notifications.filter((n) => !n.isRead).length);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleToggleNotifications = () => {
    if (!showNotifications) {
      // Only fetch when opening dropdown
      fetchNotifications();
    }
    setShowNotifications(!showNotifications);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "doctor_request":
        return {
          icon: MdPersonAdd,
          color: "text-blue-600",
          bgColor: "bg-blue-50",
        };
      case "donor_allotted":
        return {
          icon: MdPersonAdd,
          color: "text-green-600",
          bgColor: "bg-green-50",
        };
      case "donor_referred":
        return {
          icon: MdPersonAdd,
          color: "text-purple-600",
          bgColor: "bg-purple-50",
        };
      case "document_uploaded":
        return {
          icon: MdAssignment,
          color: "text-purple-600",
          bgColor: "bg-purple-50",
        };
      default:
        return {
          icon: MdWarning,
          color: "text-orange-600",
          bgColor: "bg-orange-50",
        };
    }
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleToggleNotifications}
        className="relative rounded-full bg-[#0A857D] p-3 text-white transition-all duration-200 hover:scale-105"
      >
        <Image
          src={Notification}
          alt="Notifications"
          width={20}
          height={20}
          className="h-5 w-5"
        />

        {unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 w-5 animate-pulse items-center justify-center rounded-full bg-pink-500 text-xs font-bold text-white">
            {unreadCount}
          </span>
        )}
      </button>

      {showNotifications && (
        <>
          {/* Mobile Overlay */}
          <div
            className="fixed inset-0 z-40 bg-black/20 md:hidden"
            onClick={() => setShowNotifications(false)}
          />

          <div className="fixed inset-x-4 top-20 z-50 rounded-2xl bg-white shadow-2xl ring-1 ring-black/5 md:absolute md:inset-x-auto md:right-0 md:top-auto md:mt-3 md:w-96">
            {/* Header */}
            <div className="border-b border-gray-100 px-4 py-3">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-gray-900 md:text-lg">
                  Notifications
                </h3>
                <div className="flex items-center gap-2">
                  {unreadCount > 0 && (
                    <span className="rounded-full px-2 py-1 text-xs font-medium" style={{ background: "#E9FCFB", color: "#15A79D" }}>
                      {unreadCount} new
                    </span>
                  )}
                  <button
                    onClick={() => setShowNotifications(false)}
                    className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 md:hidden"
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Notifications List */}
            <div className="max-h-80 overflow-y-auto scrollbar-hide md:max-h-96">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-[#15A79D]"></div>
                </div>
              ) : notifications.length === 0 ? (
                <div className="py-8 text-center text-gray-500">
                  <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                    <MdNotifications className="h-6 w-6 text-gray-400" />
                  </div>
                  <p className="text-sm">No notifications</p>
                </div>
              ) : (
                <div className="flex flex-col gap-2 p-3">
                {notifications.map((notification) => {
                  const iconData = getNotificationIcon(notification.type);
                  const Icon = iconData.icon;
                  return (
                    <div
                      key={notification._id}
                      className={`cursor-pointer rounded-xl px-3 py-3 transition-colors hover:bg-gray-100 md:px-4 ${
                        !notification.isRead ? "bg-gray-100" : "bg-gray-50"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={`rounded-full p-1.5 ${iconData.bgColor} flex-shrink-0 md:p-2`}
                        >
                          <Icon
                            className={`h-3.5 w-3.5 ${iconData.color} md:h-4 md:w-4`}
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm font-medium leading-tight text-gray-900">
                              {notification.title}
                            </p>
                            {!notification.isRead && (
                              <div className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-blue-500"></div>
                            )}
                          </div>
                          <p className="mt-1 text-xs leading-relaxed text-gray-600 md:text-sm">
                            {notification.message}
                          </p>
                          <p className="mt-1 text-xs text-gray-400">
                            {dayjs(notification.createdAt).fromNow()}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-100 px-3 py-3 md:px-4">
              <button
                onClick={() => { window.location.href = '/alerts'; setShowNotifications(false); }}
                className="w-full rounded-lg py-2 text-center text-sm font-medium transition-colors"
                style={{ color: "#15A79D" }}
                onMouseEnter={(e) => e.currentTarget.style.background = "#E9FCFB"}
                onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
              >
                View all notifications
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

